import { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthUser } from '../lib/auth.js';
import { setApiOrg } from '../lib/api.js';
import { useMe } from '../lib/queries.js';

export interface AuthUser {
  sub: string;
  email: string;
  /** The unit in use. A person can be in several and uses one at a time. */
  orgId?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** The unit in use, or null until one is known (picked, remembered, or their only one). */
  orgId: string | null;
  /** Use another unit: everything cached for the old one is dropped. */
  switchOrg: (orgId: string) => void;
}

const Ctx = createContext<AuthContextValue | null>(null);

const ORG_KEY = 'muster.org';

function readStoredOrg(): string | null {
  try { return localStorage.getItem(ORG_KEY); } catch { return null; }
}

function storeOrg(orgId: string): void {
  try { localStorage.setItem(ORG_KEY, orgId); } catch { /* private mode: just not remembered */ }
}

/** A notification tap opens the app at ?org=<id>, which wins over the
 *  remembered unit. The parameter is taken off the address once read. */
function initialOrg(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('org');
  if (!fromUrl) return readStoredOrg();
  storeOrg(fromUrl);
  url.searchParams.delete('org');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  return fromUrl;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<Omit<AuthUser, 'orgId'> | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(() => {
    const id = initialOrg();
    setApiOrg(id);
    return id;
  });

  async function refresh() {
    const u = await getAuthUser();
    setUser(u);
  }

  function switchOrg(id: string) {
    if (id === orgId) return;
    storeOrg(id);
    setApiOrg(id);
    setOrgId(id);
    void qc.resetQueries();
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const value = user ? { ...user, ...(orgId ? { orgId } : {}) } : null;
  return <Ctx.Provider value={{ user: value, loading, refresh, orgId, switchOrg }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

/**
 * What the signed-in user may manage, from GET /me (DB-authoritative — the
 * token's role claim is only a hint and knows nothing about team leadership).
 * Admins manage everything; a team's leader or deputy manages that team only.
 */
export function useAccess() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const ledTeam = me?.team && me.team.role !== 'member' ? me.team : null;
  return { me, isAdmin, ledTeam, canManage: isAdmin || !!ledTeam };
}
